import 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ClassNameOnlyProps } from 'module/types/common';
import {
  BodyShort01,
  HeadingCompact00,
  Outline,
  SingleLineEllipsis,
} from 'module/ui/components/styles';
import { theme } from 'module/ui/styles/theme';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { AG_GRID_LICENSE_KEY } from 'config/environment';
import { ComponentProps, FC } from 'react';
import styled, { css } from 'styled-components';

// Use constant like API_VERSION to prevent inlining
if (AG_GRID_LICENSE_KEY) {
  LicenseManager.setLicenseKey(AG_GRID_LICENSE_KEY);
}

export interface ExtraGridProps {
  autoSetHeaderHeight?: boolean;
}

type IntermediaryProps = ExtraGridProps & ComponentProps<typeof AgGridReact>;

const Intermediary: FC<React.PropsWithChildren<IntermediaryProps>> = ({
  ...rest
}) => {
  return <AgGridReact {...rest} />;
};

export const Grid: FC<
  React.PropsWithChildren<IntermediaryProps & ClassNameOnlyProps>
> = styled(Intermediary).attrs(() => ({
  headerHeight: 24,
  rowHeight: 32,
}))`
  font-family: var(--font-family);

  --ag-row-hover-color: var(--table-row-hover);
  --ag-background-color: var(--white-100);
  --ag-odd-row-background-color: var(--white-100);
  --ag-data-color: var(--gray-80);
  --grid-header-color: var(--gray-60);
  --ag-foreground-color: var(--gray-100);
  --ag-row-border-color: var(--table-border-color);
  --cell-padding: var(--spacing-04);
  --ag-range-selection-border-color: transparent;
  --ag-value-change-value-highlight-background-color: var(--blue-30);
  --grid-background: var(--white-100);
  --header-background: var(--white-100);
  --ag-selected-row-background-color: var(--blue-20);
  --cell-focus-color: var(--blue-10);
  --cell-height-small: ${theme.table.row_height.small};
  --cell-height-default: ${theme.table.row_height.medium};
  --cell-height-medium: ${theme.table.row_height.medium};
  --cell-height-large: ${theme.table.row_height.large};

  .ag-theme-balham {
    font-family: var(--font-family);

    &.ag-popup .ag-popup-editor {
      background: var(--white-100);
    }
  }

  .ag-popup {
    .ag-popup-child:not(.ag-tooltip-custom) {
      box-shadow: var(--shadow-32);
    }

    .ag-popup-child:not(.ag-tooltip-custom):not(.ag-menu) {
      border: none;
    }

    .ag-popup-child {
      z-index: var(--zindex-layer-01);
    }

    .ag-popup-editor {
      background: var(--white-100);
      min-width: 200px;
      display: flex;
      align-items: center;
      z-index: var(--zindex-layer-03);
      border-radius: 10px;
    }
  }

  .ag-cell-data-changed-animation {
    background: transparent !important;
  }

  .ag-selection-checkbox {
    opacity: 0;
    transition: opacity var(--animation-duration-medium) ease-in-out;

    input {
      cursor: pointer;
    }
  }

  .ag-row-hover .ag-selection-checkbox {
    opacity: 1;
  }

  .ag-root-wrapper,
  .ag-root {
    background: var(--grid-background);
  }

  .ag-root-wrapper,
  .ag-header {
    border: none;
  }

  .ag-header,
  .ag-status-bar {
    background: var(--header-background);
    box-shadow: none;
    position: relative;
    z-index: var(--zindex-modal-lowest);
    border-top: none;

    ${({ autoSetHeaderHeight = false }) =>
      autoSetHeaderHeight === false &&
      css`
        height: var(--height-sm) !important;
        min-height: var(--height-sm) !important;
      `};

    ${({ autoSetHeaderHeight = false }) =>
      autoSetHeaderHeight === false &&
      css`
        &,
        .ag-status-bar {
          height: var(--height-sm) !important;
          min-height: var(--height-sm) !important;
        }
      `};
  }

  .ag-status-bar {
    border-top: 1px solid var(--table-border-color);
  }

  .ag-header {
    border-bottom: 1px solid var(--table-border-color);
    ${HeadingCompact00};
    ${({ autoSetHeaderHeight = false }) =>
      autoSetHeaderHeight === false &&
      css`
        height: var(--height-sm) !important;
        min-height: var(--height-sm) !important;
      `};
  }

  .ag-row-focus.ag-row-selected,
  .ag-row-selected .ag-cell-focus {
    background: var(--ag-selected-row-background-color);
  }

  .ag-pinned-left-cols-container .ag-row,
  .ag-pinned-left-header,
  .ag-cell.ag-cell-last-left-pinned:not(.ag-cell-range-right):not(
      .ag-cell-range-single-cell
    ) {
    border-right: -1px solid var(--table-border-color);
  }

  .grid-cell-editable {
    cursor: pointer;

    .ant-tag,
    .ozone-select__multi-value__remove,
    .ozone-select__clear-indicator,
    .ozone-select__option {
      cursor: pointer;
    }
  }
  .grid-cell-not-editable {
    cursor: not-allowed;
  }
  .ag-pinned-right-header {
    border-left: 1px solid var(--border-color);
  }

  .ag-header-cell,
  .ag-cell,
  .ag-row {
    ${BodyShort01};
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .ag-header-cell-label .ag-header-custom-icon {
    margin-right: var(--spacing-03);
    display: flex;
  }

  .ag-header-cell-label .ag-header-custom-icon + .ag-header-custom-icon {
    transform: scale(0.75);
    margin-left: calc(-1 * var(--spacing-02));
  }

  .ag-header-cell {
    padding: 0 var(--cell-padding);
  }

  .ag-cell {
    padding: 0 calc(var(--cell-padding) - 1px);
  }

  .ag-row-group {
    align-items: center;
  }

  .ag-ltr .ag-group-expanded,
  .ag-ltr .ag-group-contracted {
    margin-right: var(--layout-01);
  }

  .ag-row-group-leaf-indent.ag-row-group-indent-0 {
    padding-left: var(--layout-03);
  }

  .ag-ltr [class*='ag-row-level-'] .ag-row-group-leaf-indent {
    margin-left: var(--layout-03);
  }

  .ag-ltr .ag-row-level-0 .ag-row-group-leaf-indent {
    margin-left: initial;
  }

  .ag-header-cell-label {
    ${HeadingCompact00};
    color: var(--text-tertiary);
  }

  .ag-header-cell-comp-wrapper {
    .ag-sort-none-icon {
      opacity: 0;
      transition: all 0.2s ease-in;
    }
    &:hover .ag-sort-none-icon {
      opacity: 1;
    }
  }

  .ag-row,
  .ag-header-row {
    height: 32px;
  }
  .ag-row {
    border-bottom: 1px solid var(--divider-default);
  }

  /** TODO: Remove td patchwork @artfuldev */
  .ag-react-container {
    flex-grow: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .ag-overlay-no-rows-wrapper {
    pointer-events: auto;
    .ag-react-container {
      flex-grow: 1;
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
    }
  }

  .private {
    background: var(--private-01);
  }

  .ag-cell-focus {
    background: var(----cell-focus-color);
    border: 1px solid var(--blue-30);
    --ag-border-color: var(--blue-30);
  }

  .spacer {
    display: flex;
    align-items: center;
  }

  .spacer > * {
    margin-right: var(--spacing-02) !important;
  }

  .spacer.spacer-medium > * {
    margin-right: calc(var(--spacing-03) + var(--spacing-02)) !important;
  }

  .spacer > *:last-child {
    margin-right: 0 !important;
  }

  .extra {
    background: var(--cool-gray-30);
  }

  .ant-avatar-string {
    transform: scale(0.857143) translateX(-50%);
    color: white;
    opacity: 1 !important;
  }

  .ag-row-focus {
    background: var(--ag-row-hover-color);
  }

  .ozone-select__single-value .ant-tag {
    cursor: default;
  }

  .ozone-select__dropdown-indicator,
  .ozone-select__indicator-separator {
    display: none;
  }

  .assignee > .ag-react-container {
    justify-content: center;
  }

  .highlighted {
    background-color: var(--ag-selected-row-background-color);
  }
  --ag-icon-font-family: agGridAlpine;
  --ag-checkbox-checked-color: var(--primary-color);
  --ag-checkbox-indeterminate-color: var(--primary-color);

  .ag-side-bar {
    --ag-checkbox-border-radius: var(--border-radius-default);
    --ag-range-selection-border-color: var(--primary-color);
    --ag-selected-tab-underline-color: var(--primary-color);
    --ag-selected-tab-underline-width: var(--spacing-01);
    --ag-side-bar-panel-width: 296px;
    --ag-control-panel-background-color: var(--white-100);
    --ag-side-button-selected-background-color: var(--blue-10);

    border: 0;
    box-shadow: var(--shadow-04);
    .ag-side-buttons {
      display: none;
    }

    .ag-tool-panel-wrapper {
      border: 0;
      ${BodyShort01};
      .ag-tool-panel-horizontal-resize {
        &:hover {
          background-color: var(--processing-color);
        }
      }
      .ag-column-select-header {
        border: 0;
        height: var(--input-height-lg);
        margin-bottom: var(--spacing-03);
        .ag-text-field-input-wrapper .ag-text-field-input {
          ${BodyShort01};
          max-width: 98%;
          height: var(--input-size-sm);
          padding: 0 var(--spacing-05);
          background-color: var(--gray-10);
          border: none;
          border-bottom: 1px solid var(--gray-50);
          box-shadow: var(--shadow-01);
          &:focus {
            ${Outline};
          }
        }
      }
      .ag-column-select-column {
        .ag-column-select-column-label {
          color: var(--gray-70);
        }
      }
      .ag-column-select-column-label {
        ${SingleLineEllipsis};
        max-width: 70%;
      }
      .ag-column-select-list .ag-list-item-hovered::after {
        height: 2px;
      }
      .ag-checkbox {
        .ag-checked {
          box-shadow: var(--shadow-highlight)
            rgba(var(--blue-60-in-rgb), var(--shadow-highlight-color-opacity));
          input {
            cursor: pointer;
          }
        }
      }
    }
  }
`;
